import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, useTheme } from '@mui/material';
import { Schedule, CheckCircle } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetProjects } from '../redux/sagas/requests/project';
import { statusColor } from '../utils/statusColors';
import { formatDayShort } from '../utils/dateUtils';

const ProjectList = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const ProjectCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    '&:last-child': {
        marginBottom: 0,
    },
}));

const ProjectHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
});

const ProjectName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginRight: theme.spacing(1),
    cursor: 'pointer',
    '&:hover': {
        color: theme.palette.primary.dark,
    },
}));

const StatusChip = styled(Chip)(({ theme, statuscolor }) => ({
    height: 22,
    fontSize: theme.typography.size.xs,
    backgroundColor: statuscolor,
    color: '#fff',
}));

const MetricsRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
}));

const MetricItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(CheckCircle)(({ theme }) => ({
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.5,
}));

const ClientName = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
}));

const ProjectsReadyToCloseWidget = ({ widgetId, config = {}, onRemove }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { data, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetProjects(0, 100, 'open', { progress: 100 }),
        cacheKey: 'projects-ready-to-close',
    });

    const projects = useMemo(() => (data || []).filter((project) => project.status !== 'Awaiting Authorization' && project.status !== 'Cancelled'), [data]);

    const formatDate = (date) => formatDayShort(date) || 'No deadline';

    const hasData = (projects || []).length > 0;

    return (
        <BaseWidget
            title="Projects Ready to Complete"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!hasData ? (
                <EmptyState>
                    <EmptyStateIcon />
                    <Typography variant="body2">
                        No projects ready to complete
                    </Typography>
                </EmptyState>
            ) : (
                <ProjectList>
                    {(projects || []).map((project) => (
                        <ProjectCard key={project._id}>
                            <ProjectHeader>
                                <ProjectName 
                                    onClick={() => navigate(`/account/${project.account?.slug}/project/${project._id}`)}
                                >
                                    {project.name}
                                </ProjectName>
                                <StatusChip
                                    label={project.status}
                                    size="small"
                                    statuscolor={statusColor(theme, 'project', project.status)}
                                />
                            </ProjectHeader>

                            <MetricsRow>
                                <MetricItem>
                                    <Schedule fontSize="small" />
                                    {formatDate(project.dueDate)}
                                </MetricItem>
                                {project.account?.client && (
                                    <MetricItem>
                                        <ClientName variant="caption">
                                            {project.account.client.name}
                                        </ClientName>
                                    </MetricItem>
                                )}
                            </MetricsRow>
                        </ProjectCard>
                    ))}
                </ProjectList>
            )}
        </BaseWidget>
    );
};

export default ProjectsReadyToCloseWidget;
